import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'

const LOAD_FOR_CURRENT_ENTITY_CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noEntity: 'No current entity available',
} as const

/**
 * Relays a request for the current entity to a self-contained warning request
 * with entity and scenario context.
 */
export const loadForCurrentEntity = store.effects.create(
  <
    TTypeParams extends ec.models.TypeParams.Constraint<{
      context: ents.core.admin.Context.ForScenario
    }>,
  >(
    feature: Plugin<TTypeParams>['feature'],
    plugin: Plugin<TTypeParams>,
  ) =>
    feature.actions.loadForCurrentEntity.relay(forwardTo =>
      rxjs
        .combineLatest([
          plugin.collection.plugins.currentEntityPlugin.feature.selectors.getId()
            .asObservable,
          plugin.collection.feature.selectors.context.asObservable,
        ])
        .pipe(
          rxjs.take(1),
          rxjs.map(([entityId, context]) => {
            if (
              !ec.models.EntityId.isEntityId(entityId) ||
              ec.plugins.standard.editing.models.CreationId.isCreationId(
                entityId,
              )
            ) {
              return {
                cancellation: {
                  reason: LOAD_FOR_CURRENT_ENTITY_CANCELLATION_REASON.noEntity,
                },
              }
            }

            if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
              return {
                cancellation: {
                  reason: LOAD_FOR_CURRENT_ENTITY_CANCELLATION_REASON.noContext,
                },
              }
            }

            return forwardTo(feature.actions.load, {
              input: { context, entityId },
              output: () => undefined,
            })
          }),
        ),
    ).asObservable,
)
